/**
 * Reviewer Dashboard Page
 * Overview of review workload, pending documents, and review metrics
 */

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BaseLayout } from '@/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  ClipboardCheck,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  ArrowRight,
  FileText,
  TrendingUp,
  CalendarDays,
  AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useDocuments } from '@/hooks/useDocuments';
import { ROUTES, getDocumentEditorRoute } from '@/constants/routes';
import { DOCUMENT_STATUS, formatDate, getRelativeTime } from '@/constants/beStudyMockData';
import { DocumentStatusBadge } from '@/components/be-study/common/StatusBadge';
import { SimpleProgressBar } from '@/components/be-study/common/ProgressBar';
import { ConfidenceBadge } from '@/components/be-study/common/ConfidenceIndicator';
import { cn } from '@/lib/utils';

function MetricCard({ title, value, icon: Icon, color = 'blue', subtitle }) {
  const colorMap = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    amber: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
  };

  return (
    <Card data-testid={`metric-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={cn('p-2 rounded-lg', colorMap[color])}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReviewerDashboard() {
  const { currentUser } = useAuth();
  const { documents } = useDocuments();
  const navigate = useNavigate();

  const assignedDocs = useMemo(() => {
    return documents.filter(d => d.assignedReviewer === currentUser?.id);
  }, [documents, currentUser]);

  const stats = useMemo(() => {
    const pending = assignedDocs.filter(d =>
      d.status === DOCUMENT_STATUS.PENDING_REVIEW || d.status === DOCUMENT_STATUS.IN_REVIEW
    );
    const approved = assignedDocs.filter(d => d.status === DOCUMENT_STATUS.APPROVED);
    const rejected = assignedDocs.filter(d => d.status === DOCUMENT_STATUS.REJECTED);
    return {
      pending: pending.length,
      approved: approved.length,
      rejected: rejected.length,
      total: assignedDocs.length,
    };
  }, [assignedDocs]);

  const pendingDocs = useMemo(() => {
    return assignedDocs
      .filter(d => d.status === DOCUMENT_STATUS.PENDING_REVIEW || d.status === DOCUMENT_STATUS.IN_REVIEW)
      .sort((a, b) => new Date(a.dueDate || '2999-12-31') - new Date(b.dueDate || '2999-12-31'));
  }, [assignedDocs]);

  const recentlyReviewed = useMemo(() => {
    return assignedDocs
      .filter(d => d.status === DOCUMENT_STATUS.APPROVED || d.status === DOCUMENT_STATUS.REJECTED)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 3);
  }, [assignedDocs]);

  const isOverdue = (dueDate) => dueDate && new Date(dueDate) < new Date();

  return (
    <BaseLayout title="Review Dashboard">
      <div className="p-4 space-y-4" data-testid="reviewer-dashboard">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold" data-testid="dashboard-welcome">
              Review Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Welcome back, {currentUser?.name?.split(' ').pop() || 'Reviewer'}. You have {stats.pending} document{stats.pending !== 1 ? 's' : ''} awaiting review.
            </p>
          </div>
          <Button variant="outline" asChild data-testid="view-all-reviews-btn">
            <Link to={ROUTES.DOCUMENTS}>
              View All Assigned <ArrowRight className="h-4 w-4 ml-1" />
            </Link>
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="stats-grid">
          <MetricCard title="Pending Reviews" value={stats.pending} icon={Clock} color="amber" subtitle="Needs your attention" />
          <MetricCard title="Approved" value={stats.approved} icon={CheckCircle2} color="green" />
          <MetricCard title="Rejected" value={stats.rejected} icon={XCircle} color="red" />
          <MetricCard title="Total Assigned" value={stats.total} icon={ClipboardCheck} color="blue" />
        </div>

        {/* Pending Review Queue */}
        <Card data-testid="pending-review-card">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base font-semibold">Pending Review Queue</CardTitle>
            {pendingDocs.length > 0 && (
              <span className="text-xs text-muted-foreground">{pendingDocs.length} document{pendingDocs.length !== 1 ? 's' : ''}</span>
            )}
          </CardHeader>
          <CardContent>
            {pendingDocs.length === 0 ? (
              <div className="text-center py-10" data-testid="no-pending-message">
                <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto mb-3" />
                <p className="text-sm font-medium">All caught up!</p>
                <p className="text-xs text-muted-foreground mt-1">No documents pending review.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="pending-documents-list">
                {pendingDocs.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 p-2.5 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(getDocumentEditorRoute(doc.id))}
                    data-testid={`pending-doc-${doc.id}`}
                  >
                    <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 shrink-0">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">
                        by {doc.createdByName} &middot; {doc.templateName}
                      </p>
                    </div>
                    <div className="hidden md:block w-24">
                      <SimpleProgressBar value={doc.progress} size="sm" />
                    </div>
                    <div className="hidden lg:block">
                      {doc.confidenceScore > 0 && <ConfidenceBadge score={doc.confidenceScore} size="sm" />}
                    </div>
                    <DocumentStatusBadge status={doc.status} size="sm" />
                    {doc.dueDate && (
                      <div className={cn(
                        'hidden xl:flex items-center gap-1 text-xs shrink-0',
                        isOverdue(doc.dueDate) ? 'text-red-500 font-medium' : 'text-muted-foreground'
                      )}>
                        {isOverdue(doc.dueDate) && <AlertCircle className="h-3 w-3" />}
                        <CalendarDays className="h-3 w-3" />
                        {formatDate(doc.dueDate)}
                      </div>
                    )}
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); navigate(getDocumentEditorRoute(doc.id)); }}
                      data-testid={`start-review-${doc.id}`}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Review
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recently Reviewed */}
        {recentlyReviewed.length > 0 && (
          <Card data-testid="recently-reviewed-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold">Recently Reviewed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="recently-reviewed-list">
                {recentlyReviewed.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-4 p-2.5 rounded-lg border hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => navigate(getDocumentEditorRoute(doc.id))}
                    data-testid={`reviewed-doc-${doc.id}`}
                  >
                    <div className="hidden sm:flex items-center justify-center w-10 h-10 rounded-lg bg-muted shrink-0">
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{doc.title}</p>
                      <p className="text-xs text-muted-foreground">
                        by {doc.createdByName} &middot; Reviewed {getRelativeTime(doc.updatedAt)}
                      </p>
                    </div>
                    <DocumentStatusBadge status={doc.status} size="sm" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </BaseLayout>
  );
}

export default ReviewerDashboard;
